import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CustomerIntelligenceFilters, CustomerReportType } from '../../../customerIntelligence/types';
import {
  createWhatsAppTemplate,
  getWhatsAppTemplates,
  previewWhatsAppCampaign,
  sendWhatsAppCampaign,
} from '../../services/whatsappCampaignService';
import {
  WHATSAPP_TEMPLATE_VARIABLES,
  WhatsAppCampaignPreview,
  WhatsAppCampaignSelection,
  WhatsAppCampaignSendResult,
  WhatsAppTemplate,
} from '../../types';
import {
  Actions,
  Button,
  Chip,
  Chips,
  CloseButton,
  Dialog,
  Field,
  Header,
  Overlay,
  Status,
  Summary,
} from './styles';

interface WhatsAppCampaignModalProps {
  isOpen: boolean;
  reportType: CustomerReportType;
  filters: CustomerIntelligenceFilters;
  selection: WhatsAppCampaignSelection;
  onClose: () => void;
}

const REPORT_LABELS: Record<CustomerReportType, string> = {
  'best-customers': 'Melhores Clientes',
  'purchase-frequency': 'Frequência de Compras',
  'average-ticket': 'Ticket Médio',
  'abc-curve': 'Curva ABC',
  'at-risk': 'Clientes em Risco',
  'sales-by-neighborhood': 'Vendas por Bairro',
};

function extractErrorMessage(error: unknown, fallback: string): string {
  const responseMessage = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return responseMessage || fallback;
}

const WhatsAppCampaignModal: React.FC<WhatsAppCampaignModalProps> = ({
  isOpen,
  reportType,
  filters,
  selection,
  onClose,
}) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [templateTitle, setTemplateTitle] = useState('');
  const [preview, setPreview] = useState<WhatsAppCampaignPreview | null>(null);
  const [result, setResult] = useState<WhatsAppCampaignSendResult | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewSequence = useRef(0);

  const loadTemplates = useCallback(async () => {
    try {
      const list = await getWhatsAppTemplates();
      setTemplates(list);
    } catch (err) {
      setError(extractErrorMessage(err, 'Não foi possível carregar os modelos.'));
    }
  }, []);

  const runPreview = useCallback(
    async (nextMessage: string) => {
      const sequence = previewSequence.current + 1;
      previewSequence.current = sequence;
      setLoadingPreview(true);
      setError(null);

      try {
        const nextPreview = await previewWhatsAppCampaign({
          reportType,
          filters,
          message: nextMessage,
          selection,
        });
        if (previewSequence.current !== sequence) return;
        setPreview(nextPreview);
      } catch (err) {
        if (previewSequence.current !== sequence) return;
        setPreview(null);
        setError(extractErrorMessage(err, 'Não foi possível calcular os destinatários.'));
      } finally {
        if (previewSequence.current === sequence) {
          setLoadingPreview(false);
        }
      }
    },
    [filters, reportType, selection],
  );

  useEffect(() => {
    if (!isOpen) return;

    setResult(null);
    setError(null);
    setSelectedTemplateId('');
    setTemplateTitle('');
    setMessage('');
    setPreview(null);
    loadTemplates();
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timeout = window.setTimeout(() => {
      runPreview(message);
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [isOpen, message, runPreview]);

  const insertVariable = useCallback((variable: string) => {
    const textarea = textareaRef.current;
    const token = `{{${variable}}}`;

    if (!textarea) {
      setMessage(current => `${current}${token}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setMessage(current => `${current.slice(0, start)}${token}${current.slice(end)}`);
    window.requestAnimationFrame(() => {
      textarea.focus();
      const next = start + token.length;
      textarea.setSelectionRange(next, next);
    });
  }, []);

  const handleSelectTemplate = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
      const template = templates.find(item => String(item.id) === templateId);
      if (template) {
        setMessage(template.message);
      }
    },
    [templates],
  );

  const handleSaveTemplate = useCallback(async () => {
    if (!templateTitle.trim() || !message.trim()) return;

    setSavingTemplate(true);
    setError(null);
    try {
      const created = await createWhatsAppTemplate(templateTitle.trim(), message.trim());
      setTemplates(current => [created, ...current]);
      setSelectedTemplateId(String(created.id));
      setTemplateTitle('');
    } catch (err) {
      setError(extractErrorMessage(err, 'Não foi possível salvar o modelo.'));
    } finally {
      setSavingTemplate(false);
    }
  }, [message, templateTitle]);

  const handleSend = useCallback(async () => {
    if (!message.trim() || !preview || preview.totalRecipients === 0) return;

    setSending(true);
    setError(null);
    try {
      const sent = await sendWhatsAppCampaign({
        reportType,
        filters,
        message: message.trim(),
        selection,
      });
      setResult(sent);
    } catch (err) {
      setError(extractErrorMessage(err, 'Não foi possível enviar a campanha.'));
    } finally {
      setSending(false);
    }
  }, [filters, message, preview, reportType, selection]);

  if (!isOpen) return null;

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsapp-campaign-title"
        onClick={event => event.stopPropagation()}
      >
        <Header>
          <div>
            <h3 id="whatsapp-campaign-title">Enviar WhatsApp</h3>
            <p>Campanha para {REPORT_LABELS[reportType]}</p>
          </div>
          <CloseButton type="button" aria-label="Fechar" onClick={onClose}>
            ×
          </CloseButton>
        </Header>

        {result ? (
          <>
            <Summary>
              <strong>Campanha enviada</strong>
              {result.totalSent} destinatário(s) enviados — {result.totalDuplicated} duplicado(s)
              ignorados, {result.totalInvalid} telefone(s) inválido(s).
            </Summary>
            <Actions>
              <Button type="button" onClick={onClose}>
                Fechar
              </Button>
            </Actions>
          </>
        ) : (
          <>
            <Field>
              Modelos salvos
              <select
                value={selectedTemplateId}
                onChange={event => handleSelectTemplate(event.target.value)}
              >
                <option value="">Escrever mensagem nova</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.title}
                  </option>
                ))}
              </select>
            </Field>

            <Field>
              Mensagem
              <textarea
                ref={textareaRef}
                value={message}
                onChange={event => setMessage(event.target.value)}
                placeholder="Digite a mensagem. Use as variáveis para personalizar."
              />
            </Field>

            <Chips>
              {WHATSAPP_TEMPLATE_VARIABLES.map(variable => (
                <Chip
                  key={variable.key}
                  type="button"
                  onClick={() => insertVariable(variable.key)}
                >
                  {variable.label}
                </Chip>
              ))}
            </Chips>

            <Field>
              Salvar como modelo
              <input
                value={templateTitle}
                onChange={event => setTemplateTitle(event.target.value)}
                placeholder="Título do modelo"
              />
            </Field>

            <Actions>
              <Button
                type="button"
                variant="ghost"
                disabled={!templateTitle.trim() || !message.trim() || savingTemplate}
                onClick={handleSaveTemplate}
              >
                {savingTemplate ? 'Salvando...' : 'Salvar modelo'}
              </Button>
            </Actions>

            {error && <Status error role="alert">{error}</Status>}

            <Summary aria-live="polite">
              {loadingPreview && <strong>Calculando destinatários...</strong>}
              {!loadingPreview && preview && (
                <>
                  <strong>
                    {preview.totalRecipients} destinatário(s) — {preview.totalDuplicatedPhones}{' '}
                    duplicado(s) ignorados, {preview.totalInvalidPhones} telefone(s) inválido(s)
                  </strong>
                  {preview.sampleMessage && (
                    <>
                      Prévia da mensagem
                      <pre>{preview.sampleMessage}</pre>
                    </>
                  )}
                </>
              )}
            </Summary>

            <Actions>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={!message.trim() || !preview || preview.totalRecipients === 0 || sending}
                onClick={handleSend}
              >
                {sending ? 'Enviando...' : 'Confirmar envio'}
              </Button>
            </Actions>
          </>
        )}
      </Dialog>
    </Overlay>
  );
};

export default WhatsAppCampaignModal;
